import React, { useState, useEffect } from 'react';
import { PlayCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toYouTubeEmbed } from '../lib/youtube';

export const INTRO_DEFAULTS = {
  popup_title: 'A Message from Steve',
  popup_subtitle: "Watch Steve's Message to Ward 8",
  button_text: 'A MESSAGE FROM STEVE',
  supporting_text: "Watch Steve's Message to Ward 8",
  popup_version: 'v1',
};

export const introSeenKey = (cfg) => `steve_intro_video_seen_${((cfg?.popup_version || 'v1') + '').trim() || 'v1'}`;

export const hasSeenIntro = (cfg) => {
  try { return !!localStorage.getItem(introSeenKey(cfg)); } catch { return true; }
};

export const markIntroSeen = (cfg) => {
  try { localStorage.setItem(introSeenKey(cfg), '1'); } catch { /* private mode */ }
};

// First-visit / on-demand video modal for Steve's message.
// - Mobile-first, 16:9 responsive, no horizontal overflow
// - Closable via Close button, X, overlay click, and Escape (Dialog default)
// - Autoplay only if admin enabled it, and ALWAYS muted
export const IntroVideoModal = ({ open, onOpenChange, cfg = {}, videoUrl }) => {
  const [playing, setPlaying] = useState(false);
  const autoplayMuted = cfg.autoplay_muted === 'on';
  const privacy = cfg.privacy_mode !== 'off';
  const src = toYouTubeEmbed(videoUrl, { privacy, autoplayMuted: autoplayMuted || playing });

  useEffect(() => { if (!open) setPlaying(false); }, [open]);

  const dismiss = () => {
    markIntroSeen(cfg);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) markIntroSeen(cfg); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto p-0 rounded-2xl" data-testid="intro-video-modal">
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-0 text-left space-y-1">
          <DialogTitle className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl leading-tight" data-testid="intro-video-title">
            {cfg.popup_title || INTRO_DEFAULTS.popup_title}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm font-bold text-[#C9A84C] uppercase tracking-wider" data-testid="intro-video-subtitle">
            {cfg.popup_subtitle || INTRO_DEFAULTS.popup_subtitle}
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 sm:px-6 pt-3">
          {src ? (
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-[#1a2744]">
              <iframe
                key={src}
                src={src}
                title="Watch Steve's Message to Ward 8"
                className="w-full h-full"
                loading="lazy"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                data-testid="intro-video-iframe"
              />
            </div>
          ) : (
            <div className="aspect-video w-full rounded-xl bg-[#1a2744] flex flex-col items-center justify-center text-center p-6">
              <PlayCircle className="h-12 w-12 text-[#C9A84C] mb-2" />
              <p className="text-slate-300 text-sm">Steve's video message will appear here.</p>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 px-4 sm:px-6 pb-5 pt-3">
          {src && !playing && !autoplayMuted && (
            <Button onClick={() => setPlaying(true)} className="h-12 sm:h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="intro-video-watch-btn">
              <PlayCircle className="h-4 w-4 mr-1.5" /> Watch Video
            </Button>
          )}
          <Button variant="outline" onClick={dismiss} className="h-12 sm:h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="intro-video-close-btn">
            Close
          </Button>
          <Button variant="ghost" onClick={dismiss} className="h-12 sm:h-11 text-slate-500 font-semibold btn-press" data-testid="intro-video-dontshow-btn">
            Do not show again
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
